import service from "@irati/utils/tracking/index"
import {InvoiceStatus} from "@irati/wallet/enums"
import FeatureFlag from "@irati/auth/enums/featureFlags"
import {Tenant, ProductStockStatus} from "@irati/types"

const isValidSearchTerm = (term: string) => {
  if (!term) {
    return false
  }
  if (term.trim() === "") {
    return false
  }
  return true
}

const tracking = {
  adminLogin: (adminId: string) => {
    const data = {adminId}
    service.trackEvent("internal_admin_login", data)
  },

  adminLoginWithSSO: () => {
    const data = {}
    service.trackEvent("internal_admin_login_sso", data)
  },

  adminPageView: (page: string) => {
    const data = {page}
    service.trackEvent("internal_admin_page_view", data)
  },

  changePackage: (tenant: Tenant, packageFrom: string, packageTo: string) => {
    const data = {
      packageFrom,
      packageTo,
      tenantId: tenant.uuid
    }
    service.trackEvent("internal_admin_change_package", data)
  },

  deactivateTenantUsers: (tenant: Tenant) => {
    const data = {
      tenantId: tenant.uuid
    }
    service.trackEvent("internal_admin_deactivate_tenant_users", data)
  },

  dealsSearch: (searchTerm: string) => {
    const data = {searchTerm}
    if (isValidSearchTerm(searchTerm)) {
      service.trackEvent("internal_admin_deals_search", data)
    }
  },

  giftUserWallet: (userId: string, amount: number, currency: string, reason: string) => {
    const data = {
      amount,
      currency,
      reason,
      userId
    }
    service.trackEvent("internal_admin_gift_user_wallet", data)
  },

  ghostLogin: () => {
    service.trackEvent("internal_admin_ghost_login", {})
  },

  imageUpload: () => {
    service.trackEvent("internal_admin_image_upload", {})
  },

  invoiceStatusUpdate: (status: InvoiceStatus) => {
    const data = {status}
    service.trackEvent("internal_admin_wallet_invoice_status_update", data)
  },

  reactivateTenantUsers: (tenant: Tenant) => {
    const data = {
      tenantId: tenant.uuid
    }
    service.trackEvent("internal_admin_reactivate_tenant_users", data)
  },

  resendWelcomeEmails: (tenant: Tenant) => {
    const data = {
      tenantId: tenant.uuid
    }
    service.trackEvent("internal_admin_resend_tenant_welcome_emails", data)
  },

  unlinkPerksGoCard: (cardId: string) => {
    const data = {cardId}
    service.trackEvent("internal_admin_unlink_perksGo_card", data)
  },

  stockCheck: (productId: string, status: ProductStockStatus) => {
    const data = {
      productId,
      status
    }
    service.trackEvent("internal_admin_stock_check", data)
  },

  tenantSearch: (searchTerm: string) => {
    const data = {searchTerm}
    if (isValidSearchTerm(searchTerm)) {
      service.trackEvent("internal_admin_tenant_search", data)
    }
  },

  editTenantSubdomain: (tenantId: string, adminId: string) => {
    const data = {
      tenantId,
      adminId
    }
    service.trackEvent("tenant_edit_subdomain", data)
  },

  editTenantSubdomainSuccess: (tenantId: string, adminId: string) => {
    const data = {
      tenantId,
      adminId
    }
    service.trackEvent("tenant_edit_subdomain_success", data)
  },

  editTenantStatus: (tenantId: string, adminId: string) => {
    const data = {
      tenantId,
      adminId
    }
    service.trackEvent("tenant_edit_status", data)
  },

  editTenantStatusSuccess: (tenantId: string, adminId: string) => {
    const data = {
      tenantId,
      adminId
    }
    service.trackEvent("tenant_edit_status_success", data)
  },

  toggleFeature: (tenant: Tenant, feature: string, on: boolean) => {
    const data = {
      feature,
      on,
      tenantId: tenant.uuid
    }
    service.trackEvent("internal_admin_toggle_feature", data)
  },

  toggleFeatureFlag: (tenant: Tenant, featureFlag: FeatureFlag, value: boolean) => {
    const data = {
      featureFlag,
      on: value,
      tenantId: tenant.uuid
    }
    service.trackEvent("internal_admin_toggle_feature_flag", data)
  },

  toggleProduct: (tenant: Tenant, product: string, on: boolean) => {
    const data = {
      product,
      on,
      tenantId: tenant.uuid
    }
    service.trackEvent("internal_admin_toggle_product", data)
  },

  userSearch: (searchTerm: string) => {
    const data = {searchTerm}
    if (isValidSearchTerm(searchTerm)) {
      service.trackEvent("internal_admin_user_search", data)
    }
  },

  editUserExternalID: (userId: string) => {
    const data = {
      userId
    }
    service.trackEvent("internal_admin_edit_external_id", data)
  }
}

export default tracking
